import { CargoActionForm, CargoForm } from "view/cargos/types/cargo";
import { DateUtils } from "utils/DateUtils";
import moment from "moment";
import { CallType } from "__generated__/enums";
import { intl } from "LocaleWrapper";
import { UseFormClearErrors, UseFormGetValues, UseFormSetError } from "react-hook-form";

type ActionPath = `actions.${number}.${"date" | "windowFrom"}`

const ACTION_WINDOW_DURATION_MIN = 30;
const ACTION_WINDOW_GAP_DAYS = 1;

const toTimestamp = (date: Date | string | number | null | undefined): number | null => {
	if (date == null) {
		return null;
	}
	if (typeof date === "number") {
		return date;
	}
	if (typeof date === "string") {
		return DateUtils.getTimestamp(date) ?? null;
	}
	return date.getTime();
};

type RouteInfo = {
	distanceKm?: number | null;
	transitTimeInMinutes?: number | null;
};

type RouteInfoMap = Record<number, RouteInfo>;

export const previewSimulatedTimestamps = (actions: CargoActionForm[], routeInfoMap?: RouteInfoMap) => {
	const result = actions.map((x) => ({ ...x }));

	const isUserDefined = (a: CargoActionForm) => a.simulatedTimestamp === false;
	const usesWindow = (a: CargoActionForm) => !!a.displayAsWindow;

	const currentForms = result.map((a) => {
		if (!isUserDefined(a)) {
			return null;
		}

		if (usesWindow(a)) {
			return toTimestamp(a.windowFrom);
		}

		if (a.date != null) {
			const timestamp = toTimestamp(a.date);
			return timestamp != null ? moment(timestamp).startOf("day").valueOf() : null;
		}

		return null;
	});

	const currentTos = result.map((a, i) => {
		if (!isUserDefined(a)) {
			return null;
		}

		const from = currentForms[i];
		if (from == null) {
			return null;
		}

		if (usesWindow(a)) {
			if (a.windowTo != null) {
				return a.windowTo;
			}
			return moment(from).add(ACTION_WINDOW_DURATION_MIN, "minutes").valueOf();
		}

		return moment(from).add(ACTION_WINDOW_DURATION_MIN, "minutes").valueOf();
	});

	const froms = currentForms.filter((x) => x != null);

	// preserve existing simulated dates when no user-defined dates exist
	if (froms.length === 0 && result.every(a => a.date != null || a.windowFrom != null)) {
		return result;
	}

	const todayStart = moment().startOf("day").valueOf();
	const earliestFrom = froms.sort((a, b) => a - b)[0] ?? todayStart;

	let defaultWindowTo =
		froms.length === 0
			? todayStart
			: moment(earliestFrom).subtract(ACTION_WINDOW_GAP_DAYS, "day").valueOf();

	const getNextFrom = (startIndex: number, callType: CallType) => {
		for (let i = startIndex; i < result.length; i++) {
			if (result[i].type === callType && currentForms[i] != null) {
				return currentForms[i]!;
			}
		}
		return null;
	};

	const getPreviousTo = (startIndex: number, callType: CallType) => {
		for (let i = startIndex; i >= 0; i--) {
			if (result[i].type === callType && currentTos[i] != null) {
				return currentTos[i]!;
			}
		}
		return null;
	};

	for (let i = 0; i < result.length; i++) {
		const action = result[i];

		if (isUserDefined(action)) {
			const to = currentTos[i];
			if (to != null) {
				defaultWindowTo = moment(to).add(ACTION_WINDOW_GAP_DAYS, "day").valueOf();
			}
			continue;
		}

		let simulatedFrom = defaultWindowTo;

		// Use route-based calculation if we have route info for the previous action pair
		const prevRouteInfo = i > 0 ? routeInfoMap?.[i - 1] : undefined;
		const prevAction = i > 0 ? result[i - 1] : undefined;

		if (prevRouteInfo?.transitTimeInMinutes && prevAction?.date) {
			// Previous action has a date and we have transit time - calculate based on route
			const prevDate = toTimestamp(prevAction.date);
			if (prevDate != null) {
				simulatedFrom = moment(prevDate)
					.add(prevRouteInfo.transitTimeInMinutes + 1440, "minutes")
					.valueOf();
			}
		} else if (action.type === CallType.NitrogenPurging) {
			const nextLoadingFrom = getNextFrom(i + 1, CallType.Loading);
			if (nextLoadingFrom != null) {
				simulatedFrom = moment(nextLoadingFrom).subtract(ACTION_WINDOW_GAP_DAYS, "day").valueOf();
			}
		} else if (action.type === CallType.Cleaning) {
			const prevDischargingTo = getPreviousTo(i - 1, CallType.Discharging);
			if (prevDischargingTo != null) {
				simulatedFrom = moment(prevDischargingTo).add(ACTION_WINDOW_GAP_DAYS, "day").valueOf();
			}
		}

		const simulatedTo = moment(simulatedFrom).add(ACTION_WINDOW_DURATION_MIN, "minutes").valueOf();

		if (usesWindow(action)) {
			action.windowFrom = simulatedFrom;
			action.windowTo = simulatedTo;
			action.date = null;
		} else {
			action.date = moment(simulatedFrom).format("YYYY-MM-DD");
			action.windowFrom = null;
			action.windowTo = null;
		}

		defaultWindowTo = moment(simulatedTo).add(ACTION_WINDOW_GAP_DAYS, "day").valueOf();
	}

	return result;
};

const getSequenceByType = (actionType?: CallType) => {
	switch (actionType) {
		case CallType.NitrogenPurging: return 0;
		case CallType.Loading: return 1;
		case CallType.Discharging: return 2;
		case CallType.Cleaning: return 3;
		default: return 4; // fallback??
	}
};

const getActionKey = (action: CargoActionForm, index: number) => {
	if (action.id != null && action.id !== 0) {
		return `id-${action.id}`;
	}
	return `id-${index}`;
};

export const getTimestamp = (action: CargoActionForm) => {
	const useWindow = action.displayAsWindow ?? (action.windowFrom != null && action.date == null);

	if (useWindow) {
		return action.windowFrom ?? null;
	}

	if (action.date) {
		const date = moment(new Date(action.date), "YYYY-MM-DD");
		return date.isValid() ? date.startOf("day").valueOf() : null;
	}

	return action.windowFrom ?? null;
};


export const getOrderedActions = (unorderedActions: CargoActionForm[], useSimulatedTimestamps: boolean) => {
	if (!unorderedActions || unorderedActions.length < 2) {
		return unorderedActions;
	}

	const orderByType = unorderedActions.map((x, i) => ({ action: x, index: i }))
		.sort((a, b) => {
			const typeA = getSequenceByType(a.action.type);
			const typeB = getSequenceByType(b.action.type);

			if (typeA !== typeB) {
				return typeA - typeB;
			}

			return a.index - b.index;
		}).map(x => x.action);

	const preview = useSimulatedTimestamps ? previewSimulatedTimestamps(orderByType) : orderByType;

	const orderByKey = new Map();
	preview.forEach((x, i) => {
		orderByKey.set(getActionKey(x, i), getTimestamp(x));
	});

	return orderByType.map((x, i) => ({
		action: x,
		index: i,
		type: getSequenceByType(x.type),
		key: orderByKey.get(getActionKey(x, i)) ?? null,
	}))
		.sort((a, b) => {
			if (a.type !== b.type) {
				return a.type - b.type;
			}

			if (a.key == null && b.key == null) {
				return a.index - b.index;
			}
			if (a.key == null) {
				return 1;
			}
			if (b.key == null) {
				return -1;
			}

			if (a.key !== b.key) {
				return a.key - b.key;
			}

			return a.index - b.index;
		}).map(x => x.action);
};

const getRootErrorPath = (action: CargoActionForm, index: number): ActionPath => {
	const useWindow = action.displayAsWindow ?? (action.windowFrom != null && action.date == null);

	if (useWindow) {
		return `actions.${index}.windowFrom`;
	} else {
		return `actions.${index}.date`;
	}
};

type CargoActionError = {
	path: ActionPath;
	message: string;
}

export const validateActionDates = (
	actions: CargoActionForm[],
	useSimulatedTimestamps: boolean,
): CargoActionError[] => {
	const errors: CargoActionError[] = [];

	if (actions.length === 0) {
		return errors;
	}

	const preview = useSimulatedTimestamps ? previewSimulatedTimestamps(actions) : actions;

	const timestamped = preview.map((x, i) => ({ action: x, index: i, timestamp: getTimestamp(x) }))
		.filter(x => x.timestamp != null) as { action: CargoActionForm; index: number; timestamp: number }[];

	const loadingActions = timestamped.filter(x => x.action.type === CallType.Loading);
	const dischargingActions = timestamped.filter(x => x.action.type === CallType.Discharging);
	const purgingActions = timestamped.filter(x => x.action.type === CallType.NitrogenPurging);
	const cleaningActions = timestamped.filter(x => x.action.type === CallType.Cleaning);

	const minLoadingTimestamps = Math.min(...loadingActions.map(x => x.timestamp));
	const maxDischargingTimestamps = Math.max(...dischargingActions.map(x => x.timestamp));
	const maxAllTimestamps = Math.max(...timestamped.map(x => x.timestamp));

	if (loadingActions.length === 0) {
		return errors; // can never happen since we disable the delete button
	}
	if (dischargingActions.length === 0) {
		return errors; // can never happen since we disable the delete button
	}

	loadingActions.forEach(x => {
		if (dischargingActions.some(y => y.timestamp < x.timestamp)) {
			errors.push({ path: getRootErrorPath(x.action, x.index), message: "ACTIONS_VALIDATION_ERROR_LOADING_AFTER_DISCHARGING" });
		}
		if (purgingActions.some(y => y.timestamp > x.timestamp)) {
			errors.push({ path: getRootErrorPath(x.action, x.index), message: "ACTIONS_VALIDATION_ERROR_LOADING_BEFORE_PURGING" });
		}
	});

	dischargingActions.forEach(x => {
		if (loadingActions.some(y => y.timestamp > x.timestamp)) {
			errors.push({ path: getRootErrorPath(x.action, x.index), message: "ACTIONS_VALIDATION_ERROR_DISCHARGING_BEFORE_LOADING" });
		}
	});

	purgingActions.forEach(x => {
		if (x.timestamp > minLoadingTimestamps) {
			errors.push({ path: getRootErrorPath(x.action, x.index), message: "ACTIONS_VALIDATION_ERROR_NITROGEN_PURGING_AFTER_LOADING" });
		}
	});

	cleaningActions.forEach(x => {
		if (x.timestamp < maxDischargingTimestamps) {
			errors.push({ path: getRootErrorPath(x.action, x.index), message: "ACTIONS_VALIDATION_ERROR_CLEANING_BEFORE_DISCHARGING" });
		}
		if (x.timestamp < maxAllTimestamps) {
			errors.push({ path: getRootErrorPath(x.action, x.index), message: "ACTIONS_VALIDATION_ERROR_CLEANING_NOT_LAST_ACTION" });
		}
	});

	return errors;
};

export const validateCargoActions = (
	actions: CargoActionForm[],
	setError: UseFormSetError<CargoForm>,
	clearErrors: UseFormClearErrors<CargoForm>,
	useSimulatedTimestamps: boolean,
	getValues: UseFormGetValues<CargoForm>,
) => {

	const latestActions = getValues("actions");
	const actionErrors = validateActionDates(latestActions, useSimulatedTimestamps);

	if (actionErrors.length > 0) {
		for (const error of actionErrors) {
			setError(error.path, {
				type: "validate",
				message: intl.formatMessage({ id: error.message }),
			});
		}

		return false;
	}

	actions.forEach((_, i) => {
		clearErrors([
			`actions.${i}.date`,
			`actions.${i}.windowFrom`,
			`actions.${i}.windowTo`,
		]);
	});

	return true;
};
